import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { UserContext } from '../context/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserCircle, FaEdit, FaSave, FaTimes, FaSpinner, FaCamera, FaLock } from 'react-icons/fa';
import styles from './EmployeeProfile.module.css';
import { API } from '../config';

// ============================================================
// Hardcoded dropdown options
// ============================================================
const DROPDOWN_OPTIONS = {
  role: [
    { value: 'Full_Time',  label: 'Full Time'  },
    { value: 'Part_Time',  label: 'Part Time'  },
    { value: 'Intern',     label: 'Intern'     },
    { value: 'Trainer',    label: 'Trainer'    },
    { value: 'Freelance',  label: 'Freelance'  },
  ],
  designation: [
    { value: 'Software Developer', label: 'Software Developer' },
    { value: 'Software Tester',    label: 'Software Tester'    },
    { value: 'Admin',              label: 'Admin'              },
    { value: 'HR',                 label: 'HR'                 },
  ],
};

// ============================================================
// Fields that employees CANNOT edit (office/admin fields)
// ============================================================
const NON_EDITABLE_FIELDS = [
  'official_email_id',
  'designation',
  'role',
  'department',
  'date_of_joining',
  'applied_date',
  'relieving_date',
  'client',
  'last_working_date',
  'refered_by',
  'years_worked',
];

const EmployeeProfile = () => {
  const userContext = useContext(UserContext);

  const [isEditing, setIsEditing]                 = useState(false);
  const [profileData, setProfileData]             = useState({});
  const [isEditable, setIsEditable]               = useState(true);
  const [profilePicFile, setProfilePicFile]       = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [imgTimestamp, setImgTimestamp]           = useState(Date.now());
  const [imgError, setImgError]                   = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [message, setMessage]                     = useState({ type: '', text: '' });
  const [fileUploads, setFileUploads]             = useState({});

  const fileInputRefs = useRef({});

  const currentUser = userContext?.user || userContext || {};
  const userEmail   = currentUser?.email || '';

  const getImageSrc = (filename) => {
    if (!filename) return null;
    if (filename.startsWith('http')) return filename;
    const clean = filename.replace(/^uploads\//, '');
    const url   = `${API.UPLOADS}/${clean}?t=${imgTimestamp}`;
    if (url.includes('undefined')) return null;
    return url;
  };

  const getFileHref = (val) => {
    if (!val) return '#';
    if (val.startsWith('http')) return val;
    const clean = val.replace(/^uploads\//, '');
    return `${API.UPLOADS}/${clean}`;
  };

  const fetchEmployeeData = useCallback(async () => {
    try {
      const res  = await fetch(`${API.GET_EMPLOYEE_PROFILE_BY_EMAIL}?email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success && data.data) {
        setProfileData(data.data);
        setIsEditable(data.data?.is_editable == 1);
        setImgError(false);
      } else {
        setProfileData({});
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
      setProfileData({});
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    fetchEmployeeData();
  }, [userEmail, fetchEmployeeData]);

  useEffect(() => {
    if (message.text) {
      const t = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileUploads(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePicFile(file);
    setProfilePicPreview(URL.createObjectURL(file));
    setImgError(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const form = new FormData();

      form.append('emp_id', profileData.emp_id ?? '');
      form.append('email',  profileData.official_email_id ?? profileData.email ?? userEmail);

      Object.entries(profileData).forEach(([k, v]) => {
        if (['photo', 'pancard', 'aadhaar', 'resume', 'emp_id', 'email', 'id'].includes(k)) return;
        // Skip non-editable fields — do not send them to backend
        if (NON_EDITABLE_FIELDS.includes(k)) return;
        form.append(k, v ?? '');
      });

      const fileFields = ['photo', 'pancard', 'aadhaar', 'resume'];
      fileFields.forEach(field => {
        if (fileUploads[field]) {
          form.append(field, fileUploads[field]);
        } else if (profileData[field]) {
          form.append(`existing_${field}`, profileData[field]);
        }
      });

      if (profilePicFile && !fileUploads.photo) {
        form.append('photo', profilePicFile);
      }

      const res    = await fetch(API.UPDATE_EMPLOYEE, { method: 'POST', body: form });
      const result = await res.json();

      if (result.success) {
        await fetchEmployeeData();
        setImgTimestamp(Date.now());
        setProfilePicPreview(null);
        setProfilePicFile(null);
        setFileUploads({});
        setIsEditing(false);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: result.message || 'Update failed' });
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage({ type: 'error', text: err.message || 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfilePicPreview(null);
    setProfilePicFile(null);
    setFileUploads({});
    setImgError(false);
    Object.values(fileInputRefs.current).forEach(input => input && (input.value = ''));
  };

  // Show profile only if email exists AND employee record was found in DB
  if (!userEmail || !profileData.id) return null;

  const fileFields     = ['photo', 'pancard', 'aadhaar', 'resume'];
  const dropdownFields = Object.keys(DROPDOWN_OPTIONS);
  const sidebarImageSrc = profilePicPreview || getImageSrc(profileData.photo);
  const showImage       = !!sidebarImageSrc && !imgError;

  // ============================================================
  // Render correct input type per field
  // ============================================================
  const renderInput = (key, val) => {

    // Non-editable (office/admin) field — show locked display
    if (NON_EDITABLE_FIELDS.includes(key)) {
      return (
        <div className={styles.lockedField}>
          <span className={styles.lockedValue}>{val || '-'}</span>
          <span className={styles.lockIcon} title="This field can only be changed by Admin">
            <FaLock size={11} />
          </span>
        </div>
      );
    }

    // Dropdown
    if (dropdownFields.includes(key)) {
      return (
        <select
          name={key}
          value={val || ''}
          onChange={handleChange}
          className={styles.input}
        >
          <option value="">Select {key.charAt(0).toUpperCase() + key.slice(1)}</option>
          {DROPDOWN_OPTIONS[key].map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    // File upload
    if (fileFields.includes(key)) {
      return (
        <div className={styles.fileInputWrapper}>
          {fileUploads[key] && (
            <span className={styles.newFileLabel}>✅ {fileUploads[key].name}</span>
          )}
          <input
            type="file"
            onChange={(e) => handleFileUpload(e, key)}
            className={styles.input}
            ref={el => { if (el) fileInputRefs.current[key] = el; }}
          />
        </div>
      );
    }

    // Default text input
    return (
      <input
        type="text"
        name={key}
        value={val || ''}
        onChange={handleChange}
        className={styles.input}
      />
    );
  };

  return (
    <div className={styles.profilePage}>

      {/* SIDEBAR */}
      <motion.div
        className={styles.profileSidebar}
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className={styles.sidebarPic}>
          {showImage ? (
            <img
              src={sidebarImageSrc}
              alt="Profile"
              className={styles.profileImg}
              onError={() => setImgError(true)}
            />
          ) : (
            <FaUserCircle size={90} className={styles.defaultAvatar} />
          )}

          {isEditing && (
            <label className={styles.picOverlay} title="Change profile picture">
              <FaCamera size={20} />
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfilePicChange}
                ref={el => { if (el) fileInputRefs.current.profilePic = el; }}
              />
            </label>
          )}
        </div>

        <h3 className={styles.sidebarName}>
          {profileData.firstname} {profileData.lastname}
        </h3>
        <p className={styles.sidebarDesignation}>
          {profileData.designation || profileData.role}
        </p>

        {/* Legend shown only while editing */}
        {isEditing && (
          <div className={styles.editLegend}>
            <FaLock size={11} />
            <span>Locked fields can only be edited by Admin</span>
          </div>
        )}
      </motion.div>

      {/* MAIN CONTENT */}
      <motion.div
        className={styles.profileContent}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Employee Profile</h2>

            {!isEditing && (
              <button
                className={styles.editBtn}
                onClick={() => isEditable && setIsEditing(true)}
                disabled={!isEditable}
                title={!isEditable ? 'Contact Admin to Unlock.' : 'Edit Profile'}
                style={!isEditable ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <FaEdit /> Edit
              </button>
            )}
          </div>

          <div className={styles.infoSection}>
            {Object.entries(profileData).map(([key, val]) => {
              if (['id', 'emp_id', 'is_editable', 'is_active','login_access'].includes(key)) return null;
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const isLocked = NON_EDITABLE_FIELDS.includes(key);

              return (
                <div
                  className={`${styles.infoItem} ${isEditing && isLocked ? styles.infoItemLocked : ''}`}
                  key={key}
                >
                  <span className={styles.label}>
                    {label}
                    {isEditing && isLocked && (
                      <FaLock size={10} className={styles.labelLockIcon} title="Admin only" />
                    )}
                  </span>

                  {isEditing ? (
                    renderInput(key, val)
                  ) : fileFields.includes(key) && val ? (
                    <a
                      href={getFileHref(val)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.fileLink}
                    >
                      {val.split('/').pop()?.split('\\').pop() || 'View File'}
                    </a>
                  ) : (
                    <span className={styles.value}>{val || '-'}</span>
                  )}
                </div>
              );
            })}
          </div>

          <AnimatePresence>
            {message.text && (
              <motion.div
                className={`${styles.message} ${styles[message.type]}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {message.text}
              </motion.div>
            )}
          </AnimatePresence>

          {isEditing && (
            <div className={styles.actions}>
              <button className={styles.saveBtn} onClick={handleSave} disabled={loading}>
                {loading ? <FaSpinner className={styles.spin} /> : <FaSave />} Save
              </button>
              <button className={styles.cancelBtn} onClick={handleCancel}>
                <FaTimes /> Cancel
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EmployeeProfile;